import { useEffect, useMemo, useState } from 'react';
import type { Editor } from '../editor';
import type { CatalogBlock } from '../blocks/catalog';
import { BLOCK_CATEGORIES } from '../blocks/standardLibrary';
import {
  footprintSquareMeters,
  generateBlockGeometry,
  normalizeParams,
  shelfLengthMeters,
  type ParamSpec,
} from '../blocks/parametric';
import type { CadEntity } from '../model/entity';

/**
 * 블록 라이브러리 패널 (작업지시서 P4-05)
 *
 * ── 설계 판단 ───────────────────────────────────────────────────────────
 *
 *   명령행에서 블록명을 타이핑하게 하지 않는다. 60종 코드를 외우게 하는
 *   순간 이 제품은 AutoCAD 를 대체하지 못한다. 고르고 → 클릭한다.
 *
 *   규격은 **고르기 전에** 정한다. 놓고 나서 고치게 하면 20개를 놓은 뒤
 *   전부 다시 손봐야 하는 일이 실제로 벌어진다.
 *
 *   진열 연장·점유 면적을 고르는 자리에서 미리 보여준다 — MD 가
 *   "이걸 6열 놓으면 진열이 몇 m 늘어나나" 를 도면 없이 판단한다.
 */
export function BlockLibrary({ editor }: { editor: Editor }) {
  const [, setTick] = useState(0);
  useEffect(() => editor.subscribe(() => setTick((t) => t + 1)), [editor]);

  const [category, setCategory] = useState<string>('fixture');
  const [query, setQuery] = useState('');
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [params, setParams] = useState<Record<string, number>>({});
  /** resizable 정적 블록의 "고르는 자리에서 미리 정하는" 폭(mm) — null 이면 원래 크기 그대로 */
  const [resizeWidthMm, setResizeWidthMm] = useState<number | null>(null);

  const catalog = editor.catalog;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter((b) => {
      if (q) return b.name.toLowerCase().includes(q) || b.code.toLowerCase().includes(q);
      return b.categoryCode === category;
    });
  }, [catalog, category, query]);

  const open = openCode ? catalog.find((b) => b.code === openCode) ?? null : null;

  function select(block: CatalogBlock) {
    if (openCode === block.code) {
      setOpenCode(null);
      return;
    }
    setOpenCode(block.code);
    // 규격은 스펙 기본값에서 출발한다
    setParams(block.spec ? normalizeParams(block.spec, {}) : {});
    setResizeWidthMm(block.resizable ? (block.widthMm ?? null) : null);
  }

  function place(block: CatalogBlock) {
    const initialScale =
      block.resizable && block.widthMm && resizeWidthMm && resizeWidthMm > 0
        ? resizeWidthMm / block.widthMm
        : undefined;
    editor.pickBlock(block, params, initialScale);
  }

  const pending = editor.pendingBlock;

  return (
    <div className="block-lib">
      {pending && (
        <div className="block-lib__pending" role="status">
          <span>
            <strong>{pending.block.name}</strong> 배치 중 — 도면을 클릭하세요
          </span>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => editor.clearPendingBlock()}>
            해제
          </button>
        </div>
      )}

      <input
        className="input block-lib__search"
        type="search"
        value={query}
        placeholder="블록 검색 (이름 · 코드)"
        onChange={(e) => setQuery(e.target.value)}
      />

      {query.trim() === '' && (
        <div className="block-lib__cats" role="tablist">
          {BLOCK_CATEGORIES.map((c) => (
            <button
              key={c.code}
              type="button"
              role="tab"
              aria-selected={category === c.code}
              className="block-lib__cat"
              onClick={() => setCategory(c.code)}
            >
              <span aria-hidden="true">{c.icon}</span> {c.name}
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 && <p className="cad-panel__hint">해당하는 블록이 없습니다.</p>}

      <ul className="block-lib__list">
        {visible.map((b) => (
          <li key={b.code} className={openCode === b.code ? 'block-card block-card--open' : 'block-card'}>
            <button type="button" className="block-card__head" onClick={() => select(b)} aria-expanded={openCode === b.code}>
              <BlockThumb block={b} />
              <span className="block-card__text">
                <span className="block-card__name">{b.name}</span>
                <span className="block-card__code">{b.code}</span>
              </span>
            </button>

            {openCode === b.code && open && (
              <div className="block-card__body">
                {b.spec ? (
                  <>
                    {b.spec.params.map((p) => (
                      <ParamField
                        key={p.key}
                        spec={p}
                        value={params[p.key] ?? p.default}
                        onChange={(v) => setParams((prev) => ({ ...prev, [p.key]: v }))}
                      />
                    ))}
                    <Metrics block={b} params={params} />
                  </>
                ) : b.resizable && b.widthMm ? (
                  <label className="param-row">
                    <span className="param-row__label">폭 (mm) — 원래 {b.widthMm}mm</span>
                    <input
                      className="input"
                      type="number"
                      value={resizeWidthMm ?? b.widthMm}
                      min={1}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isFinite(n) && n > 0) setResizeWidthMm(n);
                      }}
                    />
                  </label>
                ) : (
                  <p className="cad-panel__hint">규격이 고정된 기호입니다.</p>
                )}

                <Facts block={b} />

                <button type="button" className="btn btn--primary btn--block" onClick={() => place(b)}>
                  도면에 배치
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ 파라미터 */

function ParamField({
  spec,
  value,
  onChange,
}: {
  spec: ParamSpec;
  value: number;
  onChange: (v: number) => void;
}) {
  const unit = spec.unit ? ` (${spec.unit})` : '';

  // 선택형은 드롭다운 — 임의 치수를 못 넣게 막는다.
  // 모듈에 없는 폭을 넣으면 실제로 발주가 안 된다.
  if (spec.options && spec.options.length > 0) {
    return (
      <label className="param-row">
        <span className="param-row__label">
          {spec.label}
          {unit}
        </span>
        <select className="select" value={value} onChange={(e) => onChange(Number(e.target.value))}>
          {spec.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <label className="param-row">
      <span className="param-row__label">
        {spec.label}
        {unit}
      </span>
      <input
        className="input"
        type="number"
        value={value}
        min={spec.min}
        max={spec.max}
        step={spec.step ?? 1}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    </label>
  );
}

/** 고르는 자리에서 바로 보이는 지표 — 도면을 그리기 전에 판단하게 한다 */
function Metrics({ block, params }: { block: CatalogBlock; params: Record<string, number> }) {
  if (!block.spec) return null;
  const shelf = shelfLengthMeters(block.spec, params);
  const area = footprintSquareMeters(block.spec, params);
  return (
    <div className="block-card__metrics">
      {shelf > 0 && (
        <span title="이 블록 1개가 제공하는 진열 선반 총 연장">진열 {shelf.toFixed(1)} m</span>
      )}
      <span title="바닥 점유 면적">면적 {area.toFixed(2)} ㎡</span>
    </div>
  );
}

/** 설계 판단에 필요한 사실 — 이격·전력·배수 */
function Facts({ block }: { block: CatalogBlock }) {
  const c = block.clearance;
  const u = block.utility;
  const bits: string[] = [];

  if (c?.frontMm) bits.push(`전면 ${c.frontMm}mm`);
  if (c?.sideMm) bits.push(`측면 ${c.sideMm}mm`);
  if (c?.rearMm) bits.push(`후면 ${c.rearMm}mm`);

  const util: string[] = [];
  if (u?.power) util.push(`${u.power}V`);
  if (u?.water) util.push('급수');
  if (u?.drain) util.push('배수');
  if (u?.heatLoadW) util.push(`발열 ${u.heatLoadW}W`);

  const cost = (block.cost?.standardKrw ?? 0) + (block.cost?.installKrw ?? 0);

  if (bits.length === 0 && util.length === 0 && cost === 0) return null;

  return (
    <dl className="block-card__facts">
      {bits.length > 0 && (
        <>
          <dt>이격</dt>
          <dd>{bits.join(' · ')}</dd>
        </>
      )}
      {util.length > 0 && (
        <>
          <dt>설비</dt>
          <dd>{util.join(' · ')}</dd>
        </>
      )}
      {cost > 0 && (
        <>
          <dt>단가</dt>
          <dd title="자재비 + 설치비 (추정)">{cost.toLocaleString('ko-KR')}원</dd>
        </>
      )}
    </dl>
  );
}

/* -------------------------------------------------------------------- 미리보기 */

/**
 * 블록 외형을 SVG 로 그린다.
 * 실제 도형 생성기를 그대로 쓴다 — 썸네일과 도면이 어긋나면
 * 사용자가 다른 걸 고른 셈이 된다.
 */
function BlockThumb({ block }: { block: CatalogBlock }) {
  const paths = useMemo(() => thumbPaths(block), [block]);
  return (
    <svg className="block-card__thumb" viewBox="0 0 100 60" aria-hidden="true">
      {paths}
    </svg>
  );
}

function thumbPaths(block: CatalogBlock) {
  const entities = thumbEntities(block);
  if (entities.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const segs: { x1: number; y1: number; x2: number; y2: number }[] = [];
  const circles: { cx: number; cy: number; r: number }[] = [];

  const track = (x: number, y: number) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };

  for (const e of entities) {
    if (e.type === 'line') {
      segs.push({ x1: e.a.x, y1: e.a.y, x2: e.b.x, y2: e.b.y });
      track(e.a.x, e.a.y);
      track(e.b.x, e.b.y);
    } else if (e.type === 'polyline') {
      const pts = e.points;
      for (let i = 0; i < pts.length - 1; i++) {
        segs.push({ x1: pts[i]!.x, y1: pts[i]!.y, x2: pts[i + 1]!.x, y2: pts[i + 1]!.y });
      }
      if (e.closed && pts.length > 2) {
        segs.push({ x1: pts.at(-1)!.x, y1: pts.at(-1)!.y, x2: pts[0]!.x, y2: pts[0]!.y });
      }
      for (const p of pts) track(p.x, p.y);
    } else if (e.type === 'circle') {
      circles.push({ cx: e.center.x, cy: e.center.y, r: e.radius });
      track(e.center.x - e.radius, e.center.y - e.radius);
      track(e.center.x + e.radius, e.center.y + e.radius);
    }
  }

  const w = maxX - minX;
  const h = maxY - minY;
  if (!(w > 0) || !(h > 0)) return null;

  const pad = 6;
  const k = Math.min((100 - pad * 2) / w, (60 - pad * 2) / h);
  const ox = (100 - w * k) / 2;
  const oy = (60 - h * k) / 2;
  // SVG 의 y 는 아래로 증가한다. 도면과 위아래가 뒤집히면 안 된다.
  const X = (x: number) => ox + (x - minX) * k;
  const Y = (y: number) => 60 - (oy + (y - minY) * k);

  return (
    <g stroke="currentColor" strokeWidth={1} fill="none" vectorEffect="non-scaling-stroke">
      {segs.map((s, i) => (
        <line key={`l${i}`} x1={X(s.x1)} y1={Y(s.y1)} x2={X(s.x2)} y2={Y(s.y2)} />
      ))}
      {circles.map((c, i) => (
        <circle key={`c${i}`} cx={X(c.cx)} cy={Y(c.cy)} r={c.r * k} />
      ))}
    </g>
  );
}

/** 썸네일 도형은 블록당 한 번만 만든다 — 목록을 스크롤할 때마다 재생성할 이유가 없다 */
const THUMB_CACHE = new Map<string, CadEntity[]>();

function thumbEntities(block: CatalogBlock): CadEntity[] {
  const hit = THUMB_CACHE.get(block.code);
  if (hit) return hit;

  const made = block.staticEntities
    ? block.staticEntities
    : block.spec
      ? generateBlockGeometry(block.spec, {}, block.defaultLayer)
      : [];

  THUMB_CACHE.set(block.code, made);
  return made;
}
